import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import InlineChart from '../../../../charts/echarts/InlineChart'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'
import { interceptFormatters } from '../ComponentBar/formatInterceptor'
import { extractChartData } from './chartUtils'
import buildChartOptions from './buildChartOptions'
import { ChartLoading, ChartError, ChartEmpty } from './ChartStates'

function DDPTargetAllocationChart ({
  pipeline,
  variables,
  seriesAccessor,
  nameAccessor,
  actualAccessor,
  targetAccessor,
  tolLowAccessor,
  tolHighAccessor,
  allocationColorAccessor,
  valueFormat,
  heightPerBar,
  minHeight,
  allocationColor,
  outOfToleranceColor,
  targetColor,
  targetShape,
  thresholdColor,
  tooltip,
  tooltipTemplate,
  labelConfiguration,
  margin,
  className,
  style
}) {
  const { data: pipelineData, isLoading, error } = usePipelineDataContextWithVariables(pipeline, variables)
  const data = pipelineData?.data
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(
    (val) => formatter(val, valueFormat),
    [formatter, valueFormat]
  )

  const labelFormat = labelConfiguration?.format
  const labelFormatter = useCallback(
    (val) => formatter(val, labelFormat || valueFormat),
    [formatter, labelFormat, valueFormat]
  )

  const chartData = useMemo(() => {
    if (!data) return []
    return extractChartData(data, {
      seriesAccessor,
      nameAccessor,
      actualAccessor,
      targetAccessor,
      tolLowAccessor,
      tolHighAccessor,
      allocationColorAccessor
    })
  }, [
    data, seriesAccessor, nameAccessor, actualAccessor,
    targetAccessor, tolLowAccessor, tolHighAccessor, allocationColorAccessor
  ])

  const chartHeight = useMemo(() => {
    const calculated = Math.max(chartData.length, 1) * heightPerBar
    return Math.max(calculated, minHeight)
  }, [chartData.length, heightPerBar, minHeight])

  const processedTooltip = useMemo(() => {
    if (!tooltip) return undefined
    return interceptFormatters({ tooltip }, valueFormatter, valueFormatter).tooltip
  }, [tooltip, valueFormatter])

  const options = useMemo(() => {
    return buildChartOptions(chartData, {
      allocationColor,
      outOfToleranceColor,
      targetColor,
      targetShape,
      thresholdColor,
      tooltip: processedTooltip,
      tooltipTemplate,
      labelConfiguration,
      valueFormatter,
      labelFormatter,
      margin
    })
  }, [
    chartData, allocationColor, outOfToleranceColor,
    targetColor, targetShape, thresholdColor, processedTooltip,
    tooltipTemplate, labelConfiguration, valueFormatter, labelFormatter, margin
  ])

  if (isLoading) return <ChartLoading className={className} style={style} minHeight={minHeight} heightPerBar={heightPerBar} />
  if (error) return <ChartError className={className} style={style} minHeight={minHeight} />
  if (!options) return <ChartEmpty className={className} style={style} minHeight={minHeight} />

  return (
    <div className={className} style={style}>
      <InlineChart
        options={options}
        height={chartHeight}
      />
    </div>
  )
}

DDPTargetAllocationChart.propTypes = {
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,

  seriesAccessor: PropTypes.string,
  nameAccessor: PropTypes.string,
  actualAccessor: PropTypes.string,
  targetAccessor: PropTypes.string,
  tolLowAccessor: PropTypes.string,
  tolHighAccessor: PropTypes.string,
  allocationColorAccessor: PropTypes.string,

  valueFormat: PropTypes.string,
  heightPerBar: PropTypes.number,
  minHeight: PropTypes.number,
  allocationColor: PropTypes.string,
  outOfToleranceColor: PropTypes.string,
  targetColor: PropTypes.string,
  targetShape: PropTypes.oneOf(['diamond', 'circle', 'square', 'triangle']),
  thresholdColor: PropTypes.string,
  /** ECharts tooltip configuration object. Passed through to the ECharts tooltip option.
   * Format strings at tooltip.formatter or tooltip.valueFormatter are auto-converted to
   * formatter functions via interceptFormatters (e.g. tooltip={{ formatter: "0,0%" }}).
   * Any standard ECharts tooltip option (backgroundColor, textStyle, etc.) is supported. */
  tooltip: PropTypes.object,
  /** HTML template string with {token} placeholders resolved against each chart item.
   * Tokens are replaced with the item's field values before rendering as the tooltip.
   * Available tokens: {name}, {actual}, {target}, {bandStart}, {bandEnd}, {isOOT}, {color}.
   * Numeric tokens are auto-formatted via valueFormat. Falls back to default tooltip when omitted. */
  tooltipTemplate: PropTypes.string,
  labelConfiguration: PropTypes.shape({
    locationPercent: PropTypes.number,
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    format: PropTypes.string
  }),
  margin: PropTypes.object,
  className: PropTypes.string,
  style: PropTypes.object
}

DDPTargetAllocationChart.defaultProps = {
  seriesAccessor: '[0].modelToleranceGrouping[0].data',
  nameAccessor: 'levelName',
  actualAccessor: 'modelTolerance.actualPercent',
  targetAccessor: 'modelTolerance.modelPercent',
  tolLowAccessor: 'modelTolerance.itemTolLower',
  tolHighAccessor: 'modelTolerance.itemTolUpper',
  targetShape: 'diamond',
  valueFormat: 'percentage',
  heightPerBar: 80,
  minHeight: 300,
  margin: { left: 20, right: 80, top: 20, bottom: 20 }
}

export default DDPTargetAllocationChart
